import React, { useState, useEffect, useRef } from 'react';
interface TipoSerieOption {
  id: number;
  modalidad_id: number;
  nombre: string;
  created_at: string;
  updated_at: string;
}

function CustomForm() {
  const urlParams = new URLSearchParams(window.location.search);
  const id_caso = urlParams.get("id_caso");
  const id_user = urlParams.get("user_id");
  const externalApiToken = LARAVEL_TOKEN || '';
  const laravelUrl = LARAVEL_URL || '';

  const [studyInfo, setStudyInfo] = useState(null);

  const [formData, setFormData] = useState({
    id_usuario: id_user || '',
    id_caso: id_caso || '',
    uso: '',
    informe_presente: false,
    informe_ok: false,
    cumple_objetivos: false,
    ventana_ok: false,
    contraste_suficiente: false,
    grosor_ok: false,
    fecha_reciente: false,
    no_artefactado: false,
  });

  const booleanFields = [
    'informe_presente',
    'informe_ok',
    'cumple_objetivos',
    'ventana_ok',
    'contraste_suficiente',
    'grosor_ok',
    'fecha_reciente',
    'no_artefactado'
  ];

  const [typeOptions, setTypeOptions] = useState<TipoSerieOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*const ohifViewer = Object.values(window).find(obj =>
    obj?.services?.viewportGridService
  );

   if (!ohifViewer) {
    console.error('No se encontró el visor OHIF');
  } else {
    const { viewportGridService, displaySetService } = ohifViewer.services;

    const previousDisplaySetsByViewport = new Map();

    const checkForSeriesChanges = () => {
      const viewportsMap = viewportGridService.getState().viewports;

      viewportsMap.forEach((viewportData, viewportId) => {
        const newUID = viewportData.displaySetInstanceUIDs?.[0];
        const prevUID = previousDisplaySetsByViewport.get(viewportId);

        if (newUID && newUID !== prevUID) {
          previousDisplaySetsByViewport.set(viewportId, newUID);

          const displaySet = displaySetService.getDisplaySetByUID(newUID);

          if (displaySet) {
            console.log(`Serie cambiada en viewport "${viewportId}":`);
            console.log('SeriesDescription:', displaySet.SeriesDescription);
            console.log('SeriesInstanceUID:', displaySet.SeriesInstanceUID);
            console.log('StudyInstanceUID:', displaySet.StudyInstanceUID);
          } else {
            console.warn(`displaySet "${newUID}" aún no está disponible`);
          }
        }
      });
    };

    // Escuchar cambios en el estado de la grilla
    viewportGridService.subscribe(
      viewportGridService.EVENTS.GRID_STATE_CHANGED,
      () => {
        setTimeout(checkForSeriesChanges, 100);
      }
    );

    // También hacer un primer chequeo inicial
    checkForSeriesChanges();

    console.log('Listo para detectar cambios de serie con GRID_STATE_CHANGED');
  } */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const callExternalApi = async (endpoint, method = 'GET', data = null) => {
    if (!externalApiToken || !laravelUrl) {
      console.error('Token o URL de API no configurados');
      return;
    }

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-API-OHIFKEY': externalApiToken
      }
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(`${laravelUrl}${endpoint}`, options);
      if (!response.ok) {
        throw new Error(`Error en la respuesta: ${response.status} ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('Error en la llamada a la API:', error);
      throw error;
    }
  };

  const fetchTypeOptions = async () => {
    try {
      setLoading(true);
      const data = await callExternalApi('api/v1/tiposSeries');

      if (Array.isArray(data)) {
        setTypeOptions(data);
        if (data.length > 0) {
          setFormData(prevState => ({
            ...prevState,
            type: data[0].id.toString()
          }));
        }
      } else {
        throw new Error('El formato de datos recibido no es válido');
      }

      setError(null);
    } catch (err) {
      console.error('Error al cargar las opciones:', err);
      setError('No se pudieron cargar las opciones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypeOptions();
    const safeSubscribe = (service, event, callback) => {
      if (!service || typeof service.subscribe !== 'function') return () => {};
      const unsub = service.subscribe(event, callback);
      return typeof unsub === 'function' ? unsub : () => {};
    };
    const ohifViewer = Object.values(window).find(obj =>
      obj?.services?.viewportGridService
    );

    if (!ohifViewer) {
      console.error('No se encontró el visor OHIF');
      return;
    }

    const { viewportGridService, displaySetService } = ohifViewer.services;

    const previousDisplaySetsByViewport = new Map();

    const checkForSeriesChanges = async () => {
      const viewportsMap = viewportGridService.getState().viewports;

      viewportsMap.forEach(async (viewportData, viewportId) => {
        const newUID = viewportData.displaySetInstanceUIDs?.[0];
        const prevUID = previousDisplaySetsByViewport.get(viewportId);

        if (newUID && newUID !== prevUID) {
          previousDisplaySetsByViewport.set(viewportId, newUID);

          const displaySet = displaySetService.getDisplaySetByUID(newUID);

          if (displaySet) {
            console.log(`Serie cambiada en viewport "${viewportId}":`);
            console.log('SeriesDescription:', displaySet.SeriesDescription);
            console.log('SeriesInstanceUID:', displaySet.SeriesInstanceUID);
            console.log('StudyInstanceUID:', displaySet.StudyInstanceUID);

            // ✅ Limpiar campos antes de cargar nuevos datos
            setFormData(prev => ({
              ...prev,
              uso: '',
              type: '',
              informe_presente: false,
              informe_ok: false,
              cumple_objetivos: false,
              ventana_ok: false,
              contraste_suficiente: false,
              grosor_ok: false,
              fecha_reciente: false,
              no_artefactado: false
            }));

            try {
              const response = await callExternalApi(
                `api/v1/checkEstudioDicom/${displaySet.StudyInstanceUID}/${displaySet.SeriesInstanceUID}/${id_caso}`
              );

              if (response && typeof response === 'object') {
                console.log('🎯 Datos recibidos para precargar el formulario:', response);

                const booleanFields = [
                  'informe_presente',
                  'informe_ok',
                  'cumple_objetivos',
                  'ventana_ok',
                  'contraste_suficiente',
                  'grosor_ok',
                  'fecha_reciente',
                  'no_artefactado'
                ];

                setFormData(prevState => {
                  const updatedForm = { ...prevState };

                  Object.keys(prevState).forEach(key => {
                    if (response.hasOwnProperty(key)) {
                      if (booleanFields.includes(key)) {
                        updatedForm[key] = Boolean(response[key]);
                      } else {
                        updatedForm[key] = response[key];
                      }
                    }
                  });

                  return updatedForm;
                });
              }
            } catch (error) {
              console.error('Error al obtener datos de estudio:', error);
            }
          } else {
            console.warn(`displaySet "${newUID}" aún no está disponible`);
          }
        }
      });
    };

    const unsubscribe = safeSubscribe(
      viewportGridService,
      viewportGridService.EVENTS.GRID_STATE_CHANGED,
      () => setTimeout(checkForSeriesChanges, 100)
    );

    // Primer chequeo inicial
    checkForSeriesChanges();

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const ohifViewer = Object.values(window).find(obj =>
        obj?.services?.viewportGridService
      );

      if (!ohifViewer) {
        throw new Error('No se encontró el visor OHIF');
      }

      const { viewportGridService, displaySetService } = ohifViewer.services;

      const viewportsMap = viewportGridService.getState().viewports;
      const activeViewport = viewportsMap.get('default');

      if (!activeViewport) {
        throw new Error('No se encontró el viewport "default"');
      }

      const displaySetInstanceUID = activeViewport.displaySetInstanceUIDs?.[0];
      if (!displaySetInstanceUID) {
        throw new Error('No se encontró displaySetInstanceUIDs en el viewport');
      }

      const displaySet = displaySetService.getDisplaySetByUID(displaySetInstanceUID);
      if (!displaySet) {
        throw new Error(`No se encontró el displaySet con UID: ${displaySetInstanceUID}`);
      }

      const studyUID = displaySet.StudyInstanceUID;
      const seriesUID = displaySet.SeriesInstanceUID;

      const transformedFormData = {
        ...formData,
      };

      booleanFields.forEach(field => {
        transformedFormData[field] = formData[field] ? 1 : 0;
      });

      const finalData = {
        ...transformedFormData,
        uid_estudio: studyUID,
        uid_serie: seriesUID
      };

      console.log('✅ Enviando datos a la API:', finalData);

      const result = await callExternalApi('api/v1/checkEstudioDicom/store', 'POST', finalData);
      console.log('Respuesta de la API:', result);
      alert('Registro guardado exitosamente');
    } catch (error) {
      console.error('❌ Error al enviar el formulario:', error);
      alert('Hubo un error al enviar el formulario');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
        <div>
          <label className="block text-md font-medium mb-1" style={{ color: '#fff' }}>Tipo:</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded"
          >
            <option value=""> ---- </option>
            {loading ? (
              <option>Cargando...</option>
            ) : (
              typeOptions.map(option => (
                <option key={option.id} value={option.id.toString()}>
                  {option.nombre}
                </option>
              ))
            )}
          </select>
        </div>

        <div>
          <label className='block text-md font-medium mb-1' style={{ color: '#fff' }}>Uso</label>
          <select name="uso" className="w-full p-2 border border-gray-300 rounded" onChange={handleChange} value={formData.uso}>
            <option value=""> --- </option>
            <option value="segm_regfijo"> Segmentar y registro fijo</option>
            <option value="segm_regmovil"> Segmentar y registro móvil</option>
            <option value="referencia"> Usar como referencia</option>
            <option value="no_usar"> No las voy a usar </option>
          </select>
        </div>

        <div>
          <table>
            <tbody>
              {[
                { id: 'informe_presente', label: 'Tenemos informe radiológico para este estudio' },
                { id: 'informe_ok', label: 'El informe concuerda con lo que se ve' },
                { id: 'cumple_objetivos', label: 'La imagen permite cumplir los objetivos' },
                { id: 'ventana_ok', label: 'La ventana de adquisición es apropiada' },
                { id: 'contraste_suficiente', label: 'El contraste es suficiente' },
                { id: 'grosor_ok', label: 'El grosor de corte es apropiado' },
                { id: 'fecha_reciente', label: 'La fecha de las imágenes es reciente' },
                { id: 'no_artefactado', label: 'Las imágenes no están artefactadas' }
              ].map(({ id, label }) => (
                <tr key={id}>
                  <td style={{ color: '#fff' }}>{label}</td>
                  <td>
                    <input type="checkbox" name={id} checked={formData[id]} onChange={handleChange} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-full" disabled={submitting}>
          {submitting ? 'Guardando' : 'Guardar'}
        </button>
      </form>
    </div>
  );
}

export default CustomForm;
